import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  PaginationState,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table";

import { useDebounce } from "@/utils/helpers";

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 250;

export interface UseDataTableStateOptions {
  defaultSort: { id: string; desc: boolean };
  defaultPageSize?: number;
  filterDeps: unknown[];
}

export interface UseDataTableStateResult {
  search: string;
  debouncedSearch: string;
  sorting: SortingState;
  pagination: PaginationState;
  rowSelection: RowSelectionState;
  setSearch: (v: string) => void;
  setSorting: (s: SortingState) => void;
  setPagination: (p: PaginationState) => void;
  setRowSelection: (s: RowSelectionState) => void;
  clearSelection: () => void;
  orderingParam: string;
  limit: number;
  offset: number;
  selectedIds: string[];
  tableProps: {
    sorting: SortingState;
    onSortingChange: (s: SortingState) => void;
    pagination: PaginationState;
    onPaginationChange: (p: PaginationState) => void;
    rowSelection: RowSelectionState;
    onRowSelectionChange: (s: RowSelectionState) => void;
    searchValue: string;
    onSearchChange: (v: string) => void;
  };
}

export function useDataTableState(
  opts: UseDataTableStateOptions,
): UseDataTableStateResult {
  const pageSize = opts.defaultPageSize ?? DEFAULT_PAGE_SIZE;

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [sorting, setSorting] = useState<SortingState>([opts.defaultSort]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { id: defaultSortId, desc: defaultSortDesc } = opts.defaultSort;
  const orderingParam = useMemo(() => {
    const s = sorting[0] ?? { id: defaultSortId, desc: defaultSortDesc };
    return `${s.desc ? "-" : ""}${s.id}`;
  }, [sorting, defaultSortId, defaultSortDesc]);

  // Reset page + selection whenever any of these change.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setRowSelection({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterDeps is the caller's dependency list by design
  }, [debouncedSearch, orderingParam, ...opts.filterDeps]);

  const selectedIds = useMemo(() => Object.keys(rowSelection), [rowSelection]);

  const clearSelection = useCallback(() => setRowSelection({}), []);

  return {
    search,
    debouncedSearch,
    sorting,
    pagination,
    rowSelection,
    setSearch,
    setSorting,
    setPagination,
    setRowSelection,
    clearSelection,
    orderingParam,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    selectedIds,
    tableProps: {
      sorting,
      onSortingChange: setSorting,
      pagination,
      onPaginationChange: setPagination,
      rowSelection,
      onRowSelectionChange: setRowSelection,
      searchValue: search,
      onSearchChange: setSearch,
    },
  };
}
